<template>
  <eg-b-container>
    <section v-if="loading || error">
      <eg-b-row>
        <eg-b-col>
          <div v-if="loading" class="text-center">
            <eg-b-spinner class="mb-3"></eg-b-spinner>
            <p>{{ $t("common.processingRequest") }}</p>
          </div>
        </eg-b-col>
      </eg-b-row>
    </section>
    <section v-else>
      <breadcrumbs :items="breadcrumbs" />
      <eg-b-row>
        <eg-b-col lg="12" md="8">
          <div class="d-flex justify-content-between mb-3">
            <div>
              <h1 class="mb-0">{{ assignment.title }}</h1>
              <eg-b-badge
                v-if="assignment?.class"
                class="mr-2"
                pill
                variant="grey"
              >
                <eg-b-icon icon="folder"></eg-b-icon>
                {{ assignment?.class.name }}
              </eg-b-badge>
              <eg-b-badge class="mr-2" pill variant="grey">
                <eg-b-icon icon="file-text"></eg-b-icon>
                {{
                  $tc("common.essaysCount", assignment?.essaysCount || 0, {
                    count: assignment?.essaysCount || 0,
                  })
                }}
              </eg-b-badge>
              <span v-if="isGoogleAssignment" class="google-icon mr-1">
                <img src="../../assets/icons/eg-google.svg" alt="" />
              </span>
              <span class="small light-grey-text">
                {{ isGoogleAssignment ? "Imported" : "Created" }} on
                {{ createdDate }}
              </span>
            </div>

            <div>
              <eg-b-button
                :to="`/assignment/edit/${assignment.id}`"
                class="mr-2"
              >
                {{ $t("common.edit") }}
              </eg-b-button>
            </div>
          </div>
          <div v-if="essays.length > 0 || isFiltered" class="essay-box">
            <essays-table
              :essays="essays"
              :fields="$options.TABLE_FIELDS"
              :available-filters="['name', 'rubric']"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :table-loading="tableLoading"
              @move="showMoveEssayModal"
              @delete="deleteSingleEssay"
              @getData="fetchEssays"
            />

            <div class="d-flex justify-content-center">
              <eg-b-pagination
                v-model="currentPage"
                :total-rows="count"
                :per-page="limit"
                @change="fetchClassWithEssays"
              >
              </eg-b-pagination>
            </div>
          </div>

          <div v-else class="essay-box-empty">
            <h3>{{ $t("common.empty") }}</h3>
            <p>{{ $t("assignments.noEssaysGraded") }}</p>
            <eg-b-button variant="info" to="/">{{
              $t("classes.gradeNewEssay")
            }}</eg-b-button>
          </div>
        </eg-b-col>
      </eg-b-row>
    </section>

    <move-essay-modal
      :essay-id="essayToMoveId"
      @essayMoved="rerouteToClasses"
      @moveError="showError"
    />
  </eg-b-container>
</template>

<script>
import getBreadcrumbs from "./breadcrumbs";
import Breadcrumbs from "@/components/global/breadcrumbs.vue";
import EssaysTable from "@/components/essay/essays_table.vue";
import MoveEssayModal from "@/components/essay/modals/move_essay_modal.vue";
import { getAssignment, deleteEssay } from "@/api";
import formatDate from "@/utils/formatDate";
import fetchEssays from "@/mixins/fetchEssays";

const TABLE_FIELDS = [
  {
    key: "status",
    label: "Status",
    columnClass: "col-status",
    sortable: true,
    thStyle: "min-width: 164px;",
  },
  {
    key: "name",
    label: "Essay",
    tdClass: "text-truncate",
    columnClass: "col-name",
    sortable: true,
  },
  {
    key: "Class",
    label: "Class",
    columnClass: "col-rubric",
    sortable: true,
  },
  {
    key: "Rubric",
    label: "Rubric",
    columnClass: "col-rubric",
    sortable: true,
  },
  {
    key: "actions",
    label: "",
    tdClass: "text-right",
    columnClass: "col-actions",
  },
];

export default {
  name: "ClassView",
  components: {
    Breadcrumbs,
    EssaysTable,
    MoveEssayModal,
  },
  //defaultFilters, fetchEssays, isFiltered
  mixins: [fetchEssays],
  TABLE_FIELDS,
  data() {
    return {
      loading: true,
      error: "",
      assignment: {},
      createdDate: "",
      currentPage: 1,
      classId: null,
      essayToMoveId: null,
    };
  },
  computed: {
    breadcrumbs() {
      return getBreadcrumbs(this.assignment.title || "View Assignment");
    },

    isGoogleAssignment() {
      return this.assignment.type === "googleClassroom";
    },
  },
  async mounted() {
    // filter essays by class id as default
    this.defaultFilters = { AssignmentId: this.$route.params.id };
    this.fetchClassWithEssays();
  },

  methods: {
    async fetchClassWithEssays() {
      this.loading = true;

      try {
        const { data } = await getAssignment(this.$route.params.id);
        await this.fetchEssays(); // automatically fetches essays and essays this.limit, this.count, this.queuedCount

        this.assignment = data;
        this.createdDate = formatDate(data.createdAt);

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.showError(error.response?.data?.error);
      }
    },
    gradedDate(date) {
      return formatDate(date);
    },
    showMoveEssayModal(essayId) {
      this.essayToMoveId = essayId;
      this.$bvModal.show("move-essay-modal");
    },
    rerouteToClasses() {
      this.$router.push("/classes");
    },
    showError(error) {
      this.$showToastError(error.response?.data?.error);
    },
    async deleteSingleEssay(essayId) {
      try {
        this.loading = true;

        await deleteEssay(essayId);

        await this.fetchClassWithEssays();

        this.showDeleteSuccess();
      } catch (error) {
        this.loading = false;
        this.showError(error.response?.data?.error);
      }
    },
    showDeleteSuccess() {
      this.$showToastSuccess(`The essay(s) were deleted successfully.`, {
        title: "Essay(s) deleted",
        autoHideDelay: 3000,
      });
    },
  },
};
</script>
